import type { Table } from 'dexie';
import type { ShopDB } from '@/db/schema';
import type { OutboxItem, UndoEntry } from '@/db/types';

/** Working context of one optimistic apply: every write remembers the row's previous state so a rejected op can be rolled back. */
export class ApplyCtx {
  readonly undo: UndoEntry[] = [];
  readonly local: Array<{ table: string; id: string }> = [];
  private seen = new Set<string>();
  private seq = 0;
  constructor(readonly d: ShopDB, readonly item: Pick<OutboxItem, 'opId' | 'actor' | 'clientTs' | 'shopId'>, readonly bizDate: string, readonly role: string) {}

  table(name: string): Table<any, string> { return this.d.table(name); }

  /** Mirrors the server's `ctx.nextId()` sequence (derivedId(opId, n)) so ids created offline equal the ones the server will create. */
  next(derive: (opId: string, n: number) => string): string { return derive(this.item.opId, this.seq++); }
  skip(n = 1): void { this.seq += n; }

  private async remember(table: string, id: string): Promise<void> {
    const k = `${table}|${id}`;
    if (this.seen.has(k)) return;
    this.seen.add(k);
    const before = (await this.table(table).get(id)) ?? null;
    this.undo.push({ table, id, before: before ? structuredClone(before) : null });
  }

  async put<T extends { id: string }>(table: string, row: T): Promise<T> {
    await this.remember(table, row.id);
    await this.table(table).put(row);
    return row;
  }
  async patch(table: string, id: string, patch: Record<string, unknown>): Promise<Record<string, any> | null> {
    const cur = await this.table(table).get(id);
    if (!cur) return null;
    await this.remember(table, id);
    const next = { ...cur, ...patch };
    await this.table(table).put(next);
    return next;
  }
  async remove(table: string, id: string): Promise<void> {
    await this.remember(table, id);
    await this.table(table).delete(id);
  }
  /** A row that only exists for display until the server's own arrives (ledger entries, money / stock movements). */
  async addLocal<T extends { id: string }>(table: string, row: T): Promise<void> {
    await this.remember(table, row.id);
    await this.table(table).put(row);
    this.local.push({ table, id: row.id });
  }
  localId(n: number): string { return `local:${this.item.opId}:${n}`; }
}
